import { jwtDecode } from 'jwt-decode';
import { isAriaUrl, isPortalFullScreenChatPath } from './ariaUrls';
import { clearUserSession } from './sessionUtils';

export const LOGIN_REASONS = {
  session_expired: 'session_expired',
  server_unavailable: 'server_unavailable',
  logged_out: 'logged_out',
  password_changed: 'password_changed',
} as const;

export type LoginReason = (typeof LOGIN_REASONS)[keyof typeof LOGIN_REASONS];

const SESSION_REDIRECT_DEBOUNCE_MS = 2000;
let sessionRedirectInFlightUntil = 0;

const AUTH_EXEMPT_PATH_FRAGMENTS = [
  '/auth/login',
  '/auth/session',
  '/auth/refresh',
  '/auth/logout',
  '/auth/login-by-token',
  '/auth/mail-reset-token',
  '/auth/reset-forgot-password',
  '/auth/get-reset-token-by-email',
  '/auth/change-password-without-reset-token',
  '/aria/check-email',
  '/aria/initiate-signup',
  '/aria/verify-email',
  '/aria/register',
  '/user-assessment/',
  '/assessment-results/',
  '/premium/',
  // Public click-to-accept pages: no login, so a 401/404 must never bounce to the login screen.
  '/legal/',
  '/order-forms/',
  '/pay/',
];

const LOGIN_ERROR_CODES: Record<string, string> = {
  ENTITY_INACTIVE: 'Your account is inactive. Please contact your advisor for help.',
  PLATFORM_MISMATCH: 'You are not authorized for this platform.',
  RATE_LIMITED: 'Too many login attempts. Please wait a moment before trying again.',
  SERVER_UNAVAILABLE: 'Our servers are temporarily unavailable. Please wait a moment and try again.',
  SSO_EMAIL_NOT_FOUND:
    'No Aptive account is linked to this Google email. Please contact your advisor or try email and password.',
  SSO_ACCOUNT_NOT_READY:
    'Your account setup is incomplete. Please contact your advisor for help.',
  SSO_MEMBERSHIP_INELIGIBLE:
    'You do not have active organization access. Please contact your advisor for help.',
};

export function isTransientServerError(status?: number): boolean {
  return status === 502 || status === 503 || status === 504;
}

/** Client-side role guard — not a server session failure */
export function isClientPermissionDenied(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { message?: string; status?: number; isClientPermissionDenied?: boolean };
  return e.isClientPermissionDenied === true || (e.status === 403 && e.message === 'Permission Denied');
}

/** Server rejected the action for this user/role — session may still be valid */
export function isPermissionDeniedResponse(error: unknown): boolean {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (typeof data === 'string') {
    const lower = data.toLowerCase().trim();
    return lower.includes('not authorized') || lower === 'forbidden';
  }
  if (data && typeof data === 'object' && data !== null && 'message' in data) {
    const msg = String((data as { message?: unknown }).message ?? '').toLowerCase();
    return msg.includes('not authorized') || msg.includes('permission');
  }
  if (data && typeof data === 'object' && data !== null && 'error' in data) {
    const msg = String((data as { error?: unknown }).error ?? '').toLowerCase();
    return msg.includes('not authorized') || msg.includes('permission');
  }
  return false;
}

function isSessionProbeUrl(url?: string): boolean {
  if (!url) return false;
  return url.includes('/auth/session') || url.includes('/auth/refresh');
}

/**
 * True when the user should sign in again (invalid/expired/revoked token).
 * 403 from permission checks or client-side guards must NOT trigger logout.
 */
export function shouldEndSessionForAuthError(error: unknown, requestUrl?: string): boolean {
  const status = getHttpStatus(error);
  if (status === 401) return true;
  if (status !== 403) return false;
  if (isClientPermissionDenied(error)) return false;

  // authenticateToken rejects bad JWTs with sendStatus(403) on session probes
  if (isSessionProbeUrl(requestUrl)) return true;

  if (isPermissionDeniedResponse(error)) return false;

  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (data === '' || data === undefined || data === null) return true;
  if (typeof data === 'string') {
    const lower = data.toLowerCase();
    return lower.includes('authentication fail');
  }
  return false;
}

export function getHttpStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const e = error as { response?: { status?: number }; status?: number };
  return e.response?.status ?? (typeof e.status === 'number' ? e.status : undefined);
}

export function isJwtExpired(token: string, skewMs = 60_000): boolean {
  try {
    const decoded = jwtDecode<{ exp?: number }>(token);
    if (!decoded?.exp) return true;
    return decoded.exp * 1000 <= Date.now() + skewMs;
  } catch {
    return true;
  }
}

export function getStoredAuthToken(): string | undefined {
  try {
    const token = JSON.parse(localStorage.getItem('user') || '{}')?.token;
    return typeof token === 'string' && token.length > 0 ? token : undefined;
  } catch {
    return undefined;
  }
}

export function getLoginPath(): '/login' | '/aria/login' {
  if (isPortalFullScreenChatPath()) {
    return '/login';
  }
  return isAriaUrl() ? '/aria/login' : '/login';
}

export function buildLoginUrl(reason?: LoginReason): string {
  const path = getLoginPath();
  if (!reason) return path;
  const params = new URLSearchParams({ reason });
  return `${path}?${params.toString()}`;
}

export function isAuthExemptRequest(url?: string): boolean {
  if (!url) return true;
  return AUTH_EXEMPT_PATH_FRAGMENTS.some((fragment) => url.includes(fragment));
}

export function isOnLoginPage(): boolean {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname;
  return path === '/login' || path === '/aria/login';
}

export function endSessionAndRedirect(reason: LoginReason = LOGIN_REASONS.session_expired): void {
  if (typeof window === 'undefined') return;

  const now = Date.now();
  if (now < sessionRedirectInFlightUntil) return;
  sessionRedirectInFlightUntil = now + SESSION_REDIRECT_DEBOUNCE_MS;

  clearUserSession();
  window.dispatchEvent(new CustomEvent('userStateChanged', { detail: null }));

  const target = buildLoginUrl(reason);
  if (window.location.pathname + window.location.search !== target) {
    window.location.replace(target);
  }
}

export function getLoginErrorMessage(error: unknown): string {
  const status = getHttpStatus(error);
  const data = (error as { response?: { data?: unknown } })?.response?.data;

  if (data && typeof data === 'object' && data !== null) {
    const payload = data as { code?: string; message?: string };
    if (payload.code && LOGIN_ERROR_CODES[payload.code]) {
      return LOGIN_ERROR_CODES[payload.code];
    }
    if (typeof payload.message === 'string' && payload.message.length > 0) {
      return payload.message;
    }
  }

  if (typeof data === 'string' && data.length > 0 && !data.trimStart().startsWith('<')) {
    return data;
  }

  if (status === 429) {
    return LOGIN_ERROR_CODES.RATE_LIMITED;
  }

  if (isTransientServerError(status)) {
    return LOGIN_ERROR_CODES.SERVER_UNAVAILABLE;
  }

  if (!status) {
    return 'Unable to connect. Please check your network and try again.';
  }

  return 'Invalid email or password. Please try again.';
}

export function getSessionExpiredBannerMessage(): string {
  return 'You were signed out automatically. Please sign in again to access the portal.';
}

export function getPasswordChangedBannerMessage(): string {
  return 'Your password has been changed. Please sign in again with your new password.';
}
